import { Channel } from 'pusher-js'
import { useEffect, useMemo, useRef } from 'react'

import { EventHandlers } from '@bonhams1793/bonhams-typescript'

import { BonhamsChannel } from '@nx/external/pusher'

import {
  ChannelSlice,
  ChannelState,
} from '../../store/channelState/channelState'

export const useLotChannel = ({
  channelName,
  subscribe,
  unsubscribe,
  addRegistration,
  removeRegistration,
  updateChannelPayload,
  updateChannelSubscriptionStatus,
}: UseLotChannelProps) => {
  const isRegistered = useRef(false)

  const eventHandlers = useMemo(() => {
    const eventTypes = [
      'lot.bid',
      'lot.info',
      'lot.timeExtended',
      'lot.cancelBid',
      'lot.finished',
    ] as const

    const events: EventHandlers = eventTypes.reduce((handlers, eventType) => {
      handlers[eventType] = (payload) => {
        updateChannelPayload({
          channelName,
          payload: {
            ...payload,
            type: eventType,
          } as ChannelState['lastPayload'],
        })
      }
      return handlers
    }, {} as EventHandlers)

    return events
  }, [channelName, updateChannelPayload])

  useEffect(() => {
    if (!isRegistered.current) {
      const registrationCount = addRegistration(channelName)
      isRegistered.current = true

      const channel = subscribe(channelName, eventHandlers, () => {
        updateChannelSubscriptionStatus({ channelName, status: 'SUBSCRIBED' })
      })

      if (registrationCount === 1) {
        channel?.bind('pusher:subscription_error', () => {
          removeRegistration(channelName)
          isRegistered.current = false
          updateChannelSubscriptionStatus({ channelName, status: 'FAILED' })
        })
      }
    }
  }, [
    addRegistration,
    channelName,
    eventHandlers,
    removeRegistration,
    subscribe,
    unsubscribe,
    updateChannelSubscriptionStatus,
  ])

  useEffect(() => {
    return () => {
      if (isRegistered.current) {
        const registrationCount = removeRegistration(channelName)
        isRegistered.current = false

        if (!registrationCount) {
          unsubscribe(channelName)
          updateChannelSubscriptionStatus({
            channelName,
            status: 'UNSUBSCRIBED',
          })
        }
      }
    }
  }, [])
}

interface UseLotChannelProps {
  channelName: BonhamsChannel
  subscribe: (
    channelName: BonhamsChannel,
    events: EventHandlers,
    onSubscribed?: () => void
  ) => Channel | undefined
  unsubscribe: (channelName: BonhamsChannel) => void
  addRegistration: ChannelSlice['addRegistration']
  removeRegistration: ChannelSlice['removeRegistration']
  updateChannelPayload: ChannelSlice['updateChannelPayload']
  updateChannelSubscriptionStatus: ChannelSlice['updateChannelSubscriptionStatus']
}
